/**
 * Wreck Night: a procedural pixel night behind the menu and through page holes.
 *
 * Desktop attract clip: leave this empty to show the live scene. Set a same-origin
 * path such as "/assets/video/attract.mp4" to play a clip on desktop only.
 */
export const ATTRACT_VIDEO = "";

import { bayer, hex } from "../shared/color";

const TAU = Math.PI * 2;
const LOOP = 128;
const FAR_W = 384;
const MID_W = 1024;
const GND_W = 1920;
const NEB_W = 320;
const STAR_N = 32;
const BLAST_N = 20;
const MAX_LIGHT = 40;
const MAX_SPAWN = 12;
const MAX_ANT = 3;
const MAX_GEM = 10;

const SKY0 = hex("#070a17");
const SKY1 = hex("#0c1226");
const SKY2 = hex("#121b38");
const SKY3 = hex("#1a2749");
const SKY4 = hex("#2a2b4f");
const GLOW1 = hex("#4b2c3d");
const GLOW2 = hex("#7a3a2c");
const GLOW3 = hex("#c4562a");
const STAR_DIM = hex("#3a4670");
const STAR_MID = hex("#9fb0d6");
const PAPER = hex("#f3efe4");
const GOLD = hex("#ffd84d");
const TEAL = hex("#6fd0c4");
const NEB0 = hex("#16404a");
const NEB1 = hex("#2a7472");
const NEB2 = hex("#33285a");
const NEB_HI = hex("#3e8f88");
const PHI = hex("#f6dca0");
const PLIT = hex("#e0a868");
const PMID = hex("#b8744a");
const PSH = hex("#6e3f3a");
const PCORE = hex("#3a2233");
const EDGE = hex("#1a1422");
const EMBER = hex("#ff7a2e");
const EMBER_DEEP = hex("#a33b12");
const WARN = hex("#ffb089");
const MUTE = hex("#a3988c");
const WARM_MID = hex("#c9a24a");
const COOL_MID = hex("#3e8f88");
const FIRE_DEEP = hex("#5c2418");
const LINK_DIM = hex("#1d4e96");

const F = {
  sky0: "#070a17",
  sky4: "#2a2b4f",
  paper: "#f3efe4",
  gold: "#ffd84d",
  teal: "#6fd0c4",
  edge: "#1a1422",
  ember: "#ff7a2e",
  emberDeep: "#a33b12",
  warn: "#ffb089",
  red: "#e23b3b",
  mute: "#a3988c",
  mid: "#9fb0d6",
  dim: "#3a4670",
  smoke0: "#2a241f",
  smoke1: "#4a433c",
  smoke2: "#8a7d72",
  bar: "#2a241f",
  farBar: "#2c2a3a",
  farBody: "#7f8296",
  link: "#3b7de2",
  linkDim: "#1d4e96",
  towerFar: "#141a33",
  towerFarTop: "#202a4d",
  towerMid: "#0d1022",
  towerMidTop: "#1b1f38",
  ground: "#0a0910",
  groundEdge: "#2a241f",
  bevel: "#6e655c",
  core: "#3a2233",
  teal2: "#38a89c",
  winOff: "#1a1428",
  text: "#6e655c",
};

const PLANET_SHADE = [PHI, PLIT, PMID, PSH, PCORE];
const CR_U = [0.12, 0.3, 0.47, 0.62, 0.78, 0.22, 0.55];
const CR_V = [0.34, 0.5, 0.4, 0.62, 0.46, 0.68, 0.28];
const CR_DU = [0.045, 0.03, 0.05, 0.034, 0.028, 0.04, 0.026];
const CR_DV = [0.055, 0.04, 0.042, 0.05, 0.036, 0.032, 0.04];
const SPR_W = [22, 30, 38];
const SPR_H = [14, 19, 24];
const SPEED = [3, 4.5, 6];
const LAYER_P = [0.04, 0.06, 0.09];
const ARROW = [0b00010, 0b00011, 0b00101, 0b01001, 0b10001, 0b01011, 0b00110];
const PUFF_X = [1, 2, 0, 1, 2, 3, 1, 2, 0];
const PUFF_Y = [0, 0, 1, 1, 1, 1, 2, 2, 2];

type PixCanvas = {
  c: HTMLCanvasElement;
  g: CanvasRenderingContext2D;
  img: ImageData;
  data: Uint32Array;
  w: number;
  h: number;
};

type Shot = {
  on: boolean;
  x0: number;
  y0: number;
  vx: number;
  vy: number;
  t0: number;
  max: number;
  comet: boolean;
  trail: number;
};

type Bit = { x0: number; y0: number; vx: number; vy: number };
type Blast = { on: boolean; x: number; y: number; t0: number; scale: number; bits: Bit[] };
type Frag = { on: boolean; x0: number; y0: number; vx: number; vy: number; w: number; h: number; t0: number; max: number; fill: string };
type Win = {
  on: boolean;
  depth: number;
  size: number;
  far: boolean;
  variant: number;
  yFrac: number;
  anchor: number;
  bob: number;
  phase: number;
  deadUntil: number;
  glitchUntil: number;
  glitchAt: number;
  glitchN: number;
  flashUntil: number;
};

let rng = 1;
let bufW = 0;
let bufH = 0;
let horizonY = 0;
let cityY = 0;
let groundH = 0;
let farH = 0;
let midH = 0;
let nebH = 0;
let wide = true;
let portrait = false;
let low = false;
let playing = false;
let reduced = false;
let hidden = false;
let dirty = true;
let rendered = false;
let stamped = false;
let stamp = 0;
let t = 0;
let accum = 0;
let stepDt = 0;
let loopId = 0;
let layWide = false;
let layLow = false;
let lastCam = Number.NaN;
let lastAlt = Number.NaN;
let lastMs = 0;
let camX = 0;
let camPix = 1;
let destW = 1;
let altDrop = 0;
let planetR = 0;
let planetReady = 0;
let planetJob = 0;
let planetSide = 42;
let lightN = 0;
let lightSec = -1;
let spawnN = 0;
let antN = 0;
let gemN = 0;
let breakAt = 8;
let breakN = 0;
let lastBlast = -20;
let flashUntil = -1;
let poke: "" | "star" | "comet" | "blast" | "break" | "glitch" = "";
let starCount = 0;
let sparkCount = 0;
let moonX = 0;
let moonY = 0;
let moonBehind = false;
let pickX = 0;
let pickY = 0;

const view = canvas2d(8, 8);
let sky: PixCanvas | null = null;
let nebula: PixCanvas | null = null;
let stars: PixCanvas | null = null;
let flash: PixCanvas | null = null;
let farTile: HTMLCanvasElement | null = null;
let midTile: HTMLCanvasElement | null = null;
let groundTile: HTMLCanvasElement | null = null;
let moon: HTMLCanvasElement | null = null;
let planet: PixCanvas | null = null;
let nebWork: PixCanvas | null = null;
let nebX = 0;
let nebKey = "";
let farKey = "";
let midKey = "";
let gndKey = "";
let jobs = 0;
const JOB_NEB = 1;
const JOB_FAR = 2;
const JOB_MID = 4;
const JOB_GND = 8;

const nearSpr: HTMLCanvasElement[][] = [[], [], []];
const farSpr: HTMLCanvasElement[][] = [[], [], []];
const cursorSpr: HTMLCanvasElement[] = [];

const starX = new Int16Array(220);
const starY = new Int16Array(220);
const starKind = new Uint8Array(220);
const starTint = new Uint8Array(220);
const starPhase = new Float32Array(220);
const starPeriod = new Float32Array(220);

const starAt = new Float32Array(STAR_N);
const starFX = new Float32Array(STAR_N);
const starFY = new Float32Array(STAR_N);
const starAng = new Float32Array(STAR_N);
const starLife = new Float32Array(STAR_N);
const starComet = new Uint8Array(STAR_N);
let starI = 0;

const blastAt = new Float32Array(BLAST_N);
const blastFX = new Float32Array(BLAST_N);
const blastPick = new Uint8Array(BLAST_N);
let blastI = 0;

const lightX = new Int16Array(MAX_LIGHT);
const lightY = new Int16Array(MAX_LIGHT);
const lightOn = new Uint8Array(MAX_LIGHT);
const lightTint = new Uint8Array(MAX_LIGHT);
const spawnX = new Int16Array(MAX_SPAWN);
const spawnY = new Int16Array(MAX_SPAWN);
const antX = new Int16Array(MAX_ANT);
const antY = new Int16Array(MAX_ANT);
const gemX = new Int16Array(MAX_GEM);
const gemY = new Int16Array(MAX_GEM);

const ringA = new Float32Array(70);
const ringKind = new Uint8Array(70);
const ringCol = new Uint8Array(70);

const shots: Shot[] = [blankShot(), blankShot()];
const blasts: Blast[] = [blankBlast(), blankBlast()];
const frags: Frag[] = Array.from({ length: 10 }, () => blankFrag());
const wins: Win[] = [0, 1, 2, 3].map((i) => blankWin(i));

function blankShot(): Shot {
  return { on: false, x0: 0, y0: 0, vx: 0, vy: 0, t0: 0, max: 0.8, comet: false, trail: 12 };
}
function blankBlast(): Blast {
  const bits: Bit[] = [];
  for (let i = 0; i < 8; i++) bits.push({ x0: 0, y0: 0, vx: 0, vy: 0 });
  return { on: false, x: 0, y: 0, t0: 0, scale: 1, bits };
}
function blankFrag(): Frag {
  return { on: false, x0: 0, y0: 0, vx: 0, vy: 0, w: 4, h: 3, t0: 0, max: 1.2, fill: F.paper };
}
function blankWin(i: number): Win {
  return {
    on: false, depth: 0, size: 0, far: true, variant: i % 3, yFrac: 0.2, anchor: 40 + i * 80,
    bob: 3.2 + i * 0.4, phase: i * 0.7, deadUntil: 0, glitchUntil: 0, glitchAt: 1.2 + i * 1.4,
    glitchN: 0, flashUntil: 0,
  };
}

function canvas2d(w: number, h: number): CanvasRenderingContext2D {
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  const g = c.getContext("2d");
  if (!g) throw new Error("no 2d context");
  g.imageSmoothingEnabled = false;
  return g;
}

function attach(w: number, h: number): PixCanvas {
  const g = canvas2d(w, h);
  const img = g.createImageData(w, h);
  const data = new Uint32Array(img.data.buffer, img.data.byteOffset, img.data.byteLength >> 2);
  return { c: g.canvas, g, img, data, w, h };
}

function commit(b: PixCanvas) {
  b.g.putImageData(b.img, 0, 0);
}

function srand(seed: number) {
  rng = seed >>> 0 || 1;
}

function rand(): number {
  rng = (rng + 0x6d2b79f5) | 0;
  let x = Math.imul(rng ^ (rng >>> 15), 1 | rng);
  x = (x + Math.imul(x ^ (x >>> 7), 61 | x)) ^ x;
  return ((x ^ (x >>> 14)) >>> 0) / 4294967296;
}

function hash(n: number): number {
  n = Math.imul(n ^ (n >>> 16), 0x7feb352d);
  n = Math.imul(n ^ (n >>> 15), 0x846ca68b);
  return (n ^ (n >>> 16)) >>> 0;
}

function mod(n: number, m: number): number {
  if (m <= 1) return 0;
  const r = n % m;
  return r < 0 ? r + m : r;
}

function clamp(n: number, a: number, b: number): number {
  return n < a ? a : n > b ? b : n;
}

function artStep(cssW: number, cssH: number): number {
  if (cssH > cssW) return 3;
  return clamp(Math.floor(cssH / 180), 3, 5);
}

function isWide(cssW: number, cssH: number): boolean {
  return cssW >= cssH && window.innerWidth >= 1024;
}

function wrapFill(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, tile: number, fill: string) {
  if (w <= 0 || h <= 0 || tile <= 0) return;
  g.fillStyle = fill;
  if (w >= tile) {
    g.fillRect(0, y | 0, tile, h);
    return;
  }
  let x0 = x % tile;
  if (x0 < 0) x0 += tile;
  const yy = y | 0;
  if (x0 + w <= tile) g.fillRect(x0, yy, w, h);
  else {
    g.fillRect(x0, yy, tile - x0, h);
    g.fillRect(0, yy, x0 + w - tile, h);
  }
}

function skyRow(y: number, h: number): [number, number, number] {
  const stops = [0, 0.18, 0.38, 0.56, 0.72, 0.82, 0.885, 0.94, 1];
  const cols = [SKY0, SKY1, SKY2, SKY3, SKY4, GLOW1, GLOW2, GLOW3, FIRE_DEEP];
  const f = y / Math.max(1, h);
  let i = 0;
  while (i < stops.length - 2 && f >= stops[i + 1]) i++;
  const u = (f - stops[i]) / Math.max(0.0001, stops[i + 1] - stops[i]);
  const mixAt = u <= 0.4 ? 0 : (u - 0.4) / 0.6;
  return [cols[i], cols[i + 1], mixAt];
}

function buildSky(w: number, h: number): PixCanvas {
  const b = attach(w, h);
  for (let y = 0; y < h; y++) {
    const blend = skyRow(y, h);
    const row = y * w;
    if (blend[2] <= 0) {
      b.data.fill(blend[0], row, row + w);
      continue;
    }
    const q = Math.round(blend[2] * 8) / 8;
    for (let x = 0; x < w; x++) b.data[row + x] = bayer(x, y) < q ? blend[1] : blend[0];
  }
  commit(b);
  return b;
}

const noiseTab = new Map<number, Float32Array>();

function noiseTable(period: number, seed: number): Float32Array {
  const key = period * 65536 + (seed & 0xffff);
  let tab = noiseTab.get(key);
  if (!tab) {
    tab = new Float32Array(period * 128);
    for (let i = 0; i < tab.length; i++) tab[i] = hash(Math.imul(i + 1, 374761393) ^ seed) / 4294967296;
    noiseTab.set(key, tab);
  }
  return tab;
}

function vnoise(x: number, y: number, period: number, seed: number): number {
  const tab = noiseTable(period, seed);
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  let xf = x - xi;
  let yf = y - yi;
  xf = xf * xf * (3 - 2 * xf);
  yf = yf * yf * (3 - 2 * yf);
  const x0 = mod(xi, period);
  const x1 = x0 + 1 === period ? 0 : x0 + 1;
  const y0 = (yi & 127) * period;
  const y1 = ((yi + 1) & 127) * period;
  const a = tab[y0 + x0];
  const b = tab[y0 + x1];
  const c = tab[y1 + x0];
  const d = tab[y1 + x1];
  return a + (b - a) * xf + (c - a) * yf + (a - b - c + d) * xf * yf;
}

function fbm(x: number, y: number, w: number, seed: number): number {
  return (
    vnoise((x / w) * 6, y / 18, 6, seed) * 0.5 +
    vnoise((x / w) * 12, y / 9, 12, seed ^ 0x1234) * 0.3 +
    vnoise((x / w) * 24, y / 4.5, 24, seed ^ 0x9876) * 0.2
  );
}

function nebulaSlice(b: PixCanvas, from: number, to: number) {
  const w = b.w;
  const h = b.h;
  const base = h * 0.52;
  const amp = Math.min(h * 0.2, 26);
  for (let x = from; x < to && x < w; x++) {
    const ph = (x / w) * TAU;
    const center = base + Math.sin(ph) * amp + Math.sin(ph * 2 + 1.3) * amp * 0.35;
    const thick = 8 + vnoise((x / w) * 5, 3.5, 5, 0x77) * 9;
    for (let y = 0; y < h; y++) {
      const off = Math.abs(y - center) / thick;
      if (off > 2.2) continue;
      const n = fbm(x, y, w, 0x2b1);
      let d = 1 - off * 0.62 + (n - 0.5) * 1.15;
      const lane = Math.abs(vnoise((x / w) * 16, y / 3.2, 16, 0x5e) - 0.5);
      const k = bayer(x, y);
      if (d > 0.45 && lane < 0.05 && k < 0.6) d -= 0.3;
      if (d < 0.08) continue;
      let col = 0;
      if (d < 0.22) col = k < (d - 0.08) / 0.14 * 0.6 ? NEB2 : 0;
      else if (d < 0.38) col = k < (d - 0.22) / 0.16 ? NEB0 : NEB2;
      else if (d < 0.6) col = NEB0;
      else if (d < 0.76) col = k < (d - 0.6) / 0.16 ? NEB1 : NEB0;
      else if (d < 0.92) col = NEB1;
      else col = k < Math.min(0.75, (d - 0.92) * 4) ? NEB_HI : NEB1;
      if (col) b.data[y * w + x] = col;
    }
  }
}

function buildFlash(w: number): PixCanvas {
  const b = attach(w, 6);
  for (let y = 0; y < 6; y++) {
    for (let x = 0; x < w; x++) {
      const k = bayer(x, y);
      const edge = y === 0 || y === 5 ? 0.35 : 0;
      if (k > 0.9 + edge) b.data[y * w + x] = WARN;
      else if (k > 0.55 + edge) b.data[y * w + x] = GLOW3;
      else if (k > 0.2 + edge) b.data[y * w + x] = GLOW2;
    }
  }
  commit(b);
  return b;
}

function buildMoon(): HTMLCanvasElement {
  const r = 4;
  const side = 11;
  const b = attach(side, side);
  const c = 5;
  for (let y = 0; y < side; y++) {
    for (let x = 0; x < side; x++) {
      const dx = x - c;
      const dy = y - c;
      const r2 = dx * dx + dy * dy;
      if (r2 > r * r) continue;
      const inv = 1 / r;
      const nx = dx * inv;
      const ny = dy * inv;
      const nz = Math.sqrt(Math.max(0, 1 - nx * nx - ny * ny));
      const nd = nx * -0.5 + ny * -0.55 + nz * 0.67;
      const edge = r2 >= (r - 1.2) * (r - 1.2);
      let col = nd > 0.45 ? PAPER : nd > 0 ? STAR_MID : STAR_DIM;
      if (x === c - 1 && y === c + 1) col = STAR_DIM;
      if (edge && nd < 0.05) col = EDGE;
      b.data[y * side + x] = col;
    }
  }
  commit(b);
  return b.c;
}

function block(
  g: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  tile: number,
  body: string,
  top: string,
  far: boolean,
) {
  wrapFill(g, x, y, w, h, tile, F.edge);
  if (w > 2 && h > 2) wrapFill(g, x + 1, y + 1, w - 2, h - 2, tile, body);
  wrapFill(g, x + 1, y + 1, Math.max(1, w - 2), 1, tile, top);
  if (w > 4) wrapFill(g, x + w - 2, y + 2, 1, Math.max(1, h - 3), tile, F.sky0);
  const tw = Math.max(4, (w * 0.38) | 0);
  const tabRight = ((x / 7) | 0) & 1;
  const tx = tabRight ? x + w - tw - 1 : x + 1;
  wrapFill(g, tx, y - 2, tw, 2, tile, F.edge);
  wrapFill(g, tx, y - 2, tw, 1, tile, top);
  if (h > 5 && w > 6) wrapFill(g, x + 2, y + 3, Math.max(2, w - 5), 1, tile, far ? F.towerFarTop : F.sky4);
}

function punchWindows(x: number, y: number, w: number, h: number, tile: number, g: CanvasRenderingContext2D) {
  for (let yy = y + 5; yy < y + h - 1; yy += 3) {
    for (let xx = x + 2; xx < x + w - 2; xx += 3) {
      wrapFill(g, xx, yy, 1, 1, tile, F.winOff);
      if (lightN >= MAX_LIGHT) continue;
      lightX[lightN] = mod(xx, tile);
      lightY[lightN] = yy;
      lightOn[lightN] = hash(lightN * 17 + 3) % 3 === 0 ? 1 : 0;
      lightTint[lightN] = hash(lightN * 9) % 4;
      lightN++;
    }
  }
}

function jagged(
  g: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  tile: number,
  body: string,
) {
  let ww = w;
  for (let row = 0; row < h; row++) {
    wrapFill(g, x, y + row, ww, 1, tile, row < 2 ? F.edge : body);
    if (row > 1 && (row & 1) && ww > 5) ww -= 1;
  }
  wrapFill(g, x + 2, y + 2, 1, 1, tile, F.ember);
  wrapFill(g, x + Math.max(3, ww - 3), y + 3, 1, 1, tile, F.gold);
  wrapFill(g, x + 4, y + 4, 1, 1, tile, F.emberDeep);
}

const TOWER = [
  [0, 16, 7, 2, 13, 6, -1, 15, 5],
  [1, 9, 6, 0, 8, 5, 2, 7, 5, 1, 8, 5],
  [0, 15, 7, 3, 12, 8],
  [-2, 20, 8, 2, 14, 6],
  [0, 12, 6, 1, 10, 5, 0, 9, 5],
  [-1, 18, 6, 2, 13, 6, 4, 8, 5],
  [0, 8, 8, 10, 8, 7, 1, 7, 4],
  [0, 14, 6, 3, 12, 6, 6, 10, 5],
  [0, 13, 6, 2, 11, 7],
];

function shadeSkyline(g: CanvasRenderingContext2D, tileW: number, tileH: number, far: boolean) {
  const img = g.getImageData(0, 0, tileW, tileH);
  const data = new Uint32Array(img.data.buffer);
  const body = hex(far ? F.towerFar : F.towerMid);
  const off = hex(F.winOff);
  const glowA = far ? hex("#2a2140") : hex("#1d1428");
  const glowB = far ? hex("#3b2440") : hex("#2e1a26");
  const lit = [hex("#ffb089"), hex("#c9a24a"), hex("#e0a868"), hex("#3e8f88"), hex("#f3efe4"), hex("#7a3a2c")];
  const farLit = [hex("#4b2c3d"), hex("#3a4670"), hex("#7a3a2c")];
  const g0 = tileH * (far ? 0.45 : 0.55);
  for (let y = 0; y < tileH; y++) {
    const glow = y > g0 ? (y - g0) / (tileH - g0) : 0;
    for (let x = 0; x < tileW; x++) {
      const o = y * tileW + x;
      const c = data[o];
      if (c === off) {
        const floor = hash(((x / 5) | 0) * 7919 + ((y / 3) | 0) * 104729 + (far ? 3 : 0));
        if (floor % 100 < 46) {
          const roll = hash(o * 31 + 7) % 100;
          data[o] = roll < 40 ? lit[1] : roll < 62 ? lit[0] : roll < 76 ? lit[2] : roll < 88 ? lit[3] : roll < 94 ? lit[4] : lit[5];
        }
        continue;
      }
      if (c !== body) continue;
      if (far && x % 3 === 1 && y % 3 === 0 && y > 3 && hash(o * 13 + 1) % 100 < 16) {
        data[o] = farLit[hash(o) % 3];
        continue;
      }
      const k = bayer(x, y);
      if (glow > 0 && k < glow * 0.9) data[o] = glow > 0.55 && k < (glow - 0.55) * 1.4 ? glowB : glowA;
    }
  }
  g.putImageData(img, 0, 0);
}

function drawSkyline(g: CanvasRenderingContext2D, tileW: number, tileH: number, far: boolean) {
  const body = far ? F.towerFar : F.towerMid;
  const top = far ? F.towerFarTop : F.towerMidTop;
  const n = far ? 15 : 13;
  srand(far ? 0x51a7c0de : 0x71c3a11);
  for (let i = 0; i < n; i++) {
    const kind = i % 9;
    const gap = tileW / n;
    const x = Math.round(i * gap + rand() * gap * 0.22);
    const tall = far ? 0.58 + (kind % 3) * 0.14 : 0.66 + (kind % 4) * 0.08;
    const sc = Math.max(18, Math.round(tileH * tall)) / 24;
    let cursor = tileH - 1;
    const spec = TOWER[kind];
    let topBit = { xx: x, yy: cursor, ww: 10 };
    for (let k = 0; k < spec.length; k += 3) {
      const hh = Math.max(5, Math.round(spec[k + 2] * sc));
      const ww = Math.max(7, Math.round(spec[k + 1] * sc));
      const xx = x + Math.round(spec[k] * sc);
      cursor -= hh;
      const broken = (kind === 2 && k === 3) || (kind === 8 && k === 3);
      if (broken) jagged(g, xx, cursor, ww, hh, tileW, body);
      else {
        block(g, xx, cursor, ww, hh, tileW, body, top, far);
        if (!far) punchWindows(xx, cursor, ww, hh, tileW, g);
      }
      cursor -= 1;
      topBit = { xx, yy: cursor + 1, ww };
      if (kind === 6 && k === 3) wrapFill(g, x + Math.round(6 * sc), tileH - Math.round(12 * sc), Math.round(8 * sc), 2, tileW, top);
    }
    if (!far && antN < MAX_ANT && (kind === 1 || kind === 4 || kind === 5)) {
      const ax = mod(topBit.xx + (topBit.ww >> 1), tileW);
      wrapFill(g, ax, topBit.yy - 7, 1, 7, tileW, F.towerMidTop);
      antX[antN] = ax;
      antY[antN] = topBit.yy - 8;
      antN++;
    }
    if (!far && (kind === 2 || kind === 8) && spawnN < MAX_SPAWN) {
      spawnX[spawnN] = mod(topBit.xx + 2, tileW);
      spawnY[spawnN] = topBit.yy;
      spawnN++;
    }
  }
}

function buildGround(w: number, h: number): HTMLCanvasElement {
  const b = attach(w, h);
  const rim = hex("#7a3a2c");
  const rim2 = hex("#3a1e22");
  const dirt = hex("#0a0910");
  const chunk = hex("#16121c");
  for (let x = 0; x < w; x++) {
    const ph = (x / w) * TAU;
    const mound = 2.2 + Math.sin(ph * 9) * 1.3 + Math.sin(ph * 23 + 0.7) * 0.8 + vnoise((x / w) * 160, 0.5, 160, 0x91) * 1.6;
    const e = clamp(Math.round(mound), 0, h - 3);
    for (let y = e; y < h; y++) {
      let col = dirt;
      if (y === e) col = hash(x * 7 + 1) % 6 === 0 ? hex("#c4562a") : rim;
      else if (y === e + 1) col = bayer(x, y) < 0.5 ? rim2 : dirt;
      else if (hash(((x / 3) | 0) * 131 + ((y / 2) | 0) * 71) % 9 === 0) col = chunk;
      b.data[y * w + x] = col;
    }
  }
  commit(b);
  const g = b.g;
  gemN = 0;
  const props = 14;
  for (let i = 0; i < props; i++) {
    const kind = i % 9;
    const x = Math.round((i + 0.2) * (w / props));
    const y = 3 + (i % 3);
    if (kind === 0 || kind === 4) {
      const pw = kind === 0 ? 16 : 11;
      const ph = kind === 0 ? 7 : 5;
      wrapFill(g, x, y, pw, ph, w, F.edge);
      wrapFill(g, x + 1, y + 1, pw - 2, ph - 2, w, F.bar);
      wrapFill(g, x + 2, y + 2, Math.max(2, pw - 6), 1, w, "#4a433c");
      wrapFill(g, x + pw - 4, y + 1, 2, 2, w, F.ember);
    } else if (kind === 1 || kind === 8) {
      wrapFill(g, x, y, 2, h - y - 1, w, "#3a3340");
      wrapFill(g, x + 2, y + 2, 6, 2, w, "#4a433c");
      wrapFill(g, x + 6, y + 3, 2, h - y - 4, w, F.edge);
    } else if (kind === 2 || kind === 6) {
      const rad = kind === 2 ? 4 : 3;
      for (let k = 0; k < 8; k++) {
        const a = (k / 8) * TAU;
        wrapFill(g, x + 6 + Math.round(Math.cos(a) * rad), y + 2 + Math.round(Math.sin(a) * rad), 1, 1, w, k % 2 ? F.teal2 : "#3a3340");
      }
    } else if (kind === 3) {
      wrapFill(g, x, y + 1, 12, 3, w, F.edge);
      wrapFill(g, x + 1, y + 2, 5, 1, w, F.towerFarTop);
      wrapFill(g, x + 8, y, 3, 2, w, "#4a433c");
    } else if (kind === 5) {
      for (let k = 0; k < 8; k++) wrapFill(g, x + k, y + (k >> 1), 2, 1, w, k < 2 ? F.edge : "#3a3340");
    } else {
      wrapFill(g, x, y, 4, h - y, w, F.edge);
      wrapFill(g, x + 1, y + 1, 2, 3, w, "#2c2a3a");
      wrapFill(g, x + 3, y + 2, 1, 1, w, F.ember);
    }
    if (gemN < MAX_GEM && (kind === 0 || kind === 7 || kind === 2)) {
      gemX[gemN] = mod(x + 3, w);
      gemY[gemN] = Math.min(h - 2, y + 2);
      gemN++;
    }
  }
  return b.c;
}

function bitten(x: number, y: number, w: number, h: number, variant: number): boolean {
  if (variant === 0) return x > w - 8 && y < 7 && w - x + y < 8;
  if (variant === 1) return x < 7 && y > h - 7 && x + (h - y) < 7;
  return x > w - 6 && y > 4 && y < h - 3 && (x + y) % 5 > 2;
}

function paintWindow(w: number, h: number, variant: number, far: boolean): HTMLCanvasElement {
  const b = attach(w, h);
  const bar = far ? hex("#2c2a3a") : hex("#2a241f");
  const body = far ? hex("#7f8296") : PAPER;
  const text = far ? hex("#2a241f") : MUTE;
  const link = far ? LINK_DIM : hex("#3b7de2");
  const set = (x: number, y: number, col: number) => {
    if (x < 0 || y < 0 || x >= w || y >= h) return;
    b.data[y * w + x] = col;
  };
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const bite = bitten(x, y, w, h, variant);
      const neighbor = bite && (
        (x > 0 && !bitten(x - 1, y, w, h, variant)) ||
        (y > 0 && !bitten(x, y - 1, w, h, variant)) ||
        (x + 1 < w && !bitten(x + 1, y, w, h, variant)) ||
        (y + 1 < h && !bitten(x, y + 1, w, h, variant))
      );
      if (bite) {
        if (neighbor) set(x, y, EDGE);
        continue;
      }
      const innerBite = !bite && (
        bitten(x + 1, y, w, h, variant) || bitten(x, y + 1, w, h, variant) || bitten(x - 1, y, w, h, variant)
      );
      if (x === 0 || y === 0 || x === w - 1 || y === h - 1) {
        set(x, y, EDGE);
        continue;
      }
      if (innerBite) {
        set(x, y, far ? STAR_MID : WARN);
        continue;
      }
      if (y <= 3) {
        set(x, y, bar);
        continue;
      }
      set(x, y, body);
      const crack = Math.abs(y - 4 - Math.round((x / w) * (h - 6))) <= 0 && y > 4;
      if (crack) set(x, y, EDGE);
    }
  }
  const btn = variant === 0 ? 3 : w - 5;
  set(btn, 1, EMBER);
  set(btn + 1, 1, EMBER);
  set(btn, 2, EMBER_DEEP);
  set(btn + 1, 2, EMBER);
  for (let x = 3; x < w - 6; x += 1) if (!bitten(x, 4, w, h, variant)) set(x, 4, text);
  const lines = Math.min(4, Math.floor((h - 8) / 2));
  for (let i = 0; i < lines; i++) {
    const y = 6 + i * 2;
    const len = Math.max(4, w - 8 - ((i * 3) % 5));
    const col = i === lines - 1 ? link : text;
    for (let x = 3; x < 3 + len && x < w - 2; x++) if (!bitten(x, y, w, h, variant)) set(x, y, col);
  }
  if (w >= 30) {
    for (let y = h - 7; y < h - 3; y++) {
      for (let x = w - 10; x < w - 4; x++) if (!bitten(x, y, w, h, variant)) set(x, y, y === h - 7 ? hex("#16404a") : hex("#38a89c"));
    }
  }
  commit(b);
  return b.c;
}

function buildWindows() {
  for (let size = 0; size < 3; size++) {
    for (let v = 0; v < 3; v++) {
      nearSpr[size][v] = paintWindow(SPR_W[size], SPR_H[size], v, false);
      farSpr[size][v] = paintWindow(SPR_W[size], SPR_H[size], v, true);
    }
  }
}

function buildStars() {
  starCount = 0;
  sparkCount = 0;
  srand(0x5a11);
  const top = bufH * 0.62;
  const glow = Math.max(top + 4, horizonY);
  const attempts = Math.round((bufW * top) / 110);
  for (let n = 0; n < attempts && starCount < 200; n++) {
    const x = (rand() * bufW) | 0;
    const y = (rand() * top) | 0;
    const keep = 1 - y / glow;
    if (rand() > keep) continue;
    const roll = rand();
    let kind = 0;
    if (roll < 0.03 && sparkCount < 6) {
      kind = 2;
      sparkCount++;
    } else if (roll < 0.25) kind = 1;
    starX[starCount] = x;
    starY[starCount] = y;
    starKind[starCount] = kind;
    const tint = rand();
    starTint[starCount] = tint < 0.1 ? 1 : tint < 0.2 ? 2 : 0;
    starPhase[starCount] = rand();
    starPeriod[starCount] = 2.5 + rand() * 4.5;
    starCount++;
  }
  if (sparkCount < 6) {
    starX[starCount] = Math.max(2, (bufW * (wide ? 0.08 : 0.18)) | 0);
    starY[starCount] = Math.max(2, (bufH * (wide ? 0.12 : 0.03)) | 0);
    starKind[starCount] = 2;
    starTint[starCount] = 0;
    starPhase[starCount] = 0.2;
    starPeriod[starCount] = 3.5;
    starCount++;
    sparkCount++;
  }
}

function writePlanetFrame(frame: number) {
  if (!planet) return;
  const side = planetSide;
  const r = planetR;
  const cx = r + 1;
  const cuts = [0.62, 0.28, 0.02, -0.28];
  for (let y = 0; y < side; y++) {
    for (let x = 0; x < side; x++) {
      const o = (frame * side + y) * side + x;
      const dx = x - cx;
      const dy = y - cx;
      const r2 = dx * dx + dy * dy;
      if (r2 > r * r) {
        planet.data[o] = 0;
        continue;
      }
      const inv = 1 / r;
      const nx = dx * inv;
      const ny = dy * inv;
      const nz = Math.sqrt(Math.max(0, 1 - nx * nx - ny * ny));
      const nd = nx * -0.52 + ny * -0.58 + nz * 0.63;
      let s = nd > 0.62 ? 0 : nd > 0.28 ? 1 : nd > 0.02 ? 2 : nd > -0.28 ? 3 : 4;
      for (let k = 0; k < 4; k++) {
        if (Math.abs(nd - cuts[k]) < 0.06) {
          s = bayer(x, y) < 0.5 ? k : k + 1;
          break;
        }
      }
      if (((((ny + 1) * 1.5) | 0) & 1) === 0 && s < 4) s++;
      let u = Math.atan2(nx, nz) / TAU;
      if (u < 0) u += 1;
      u += frame / 64;
      if (u >= 1) u -= 1;
      const v = (ny + 1) * 0.5;
      for (let c = 0; c < 7; c++) {
        let du = Math.abs(u - CR_U[c]);
        if (du > 0.5) du = 1 - du;
        if (du < CR_DU[c] && Math.abs(v - CR_V[c]) < CR_DV[c] && s < 4) s++;
      }
      if (s > 4) s = 4;
      const edge = r2 >= (r - 1.15) * (r - 1.15);
      const ang = Math.atan2(dy, dx);
      let col = PLANET_SHADE[s];
      if (edge && ang > 0.42 && ang < 2.7 && nd < 0.22) col = TEAL;
      else if (edge && nd < 0.1) col = EDGE;
      else if (r2 >= (r - 3) * (r - 3) && nd > 0.4) col = PHI;
      planet.data[o] = col;
    }
  }
}

function buildPlanet(r: number) {
  planetJob++;
  planetR = r;
  planetReady = 0;
  const side = r * 2 + 3;
  planetSide = side;
  planet = attach(side, side * 64);
  writePlanetFrame(0);
  planetReady = 1;
  commit(planet);
  const job = planetJob;
  let frame = 1;
  const step = (deadline?: IdleDeadline) => {
    if (job !== planetJob || !planet) return;
    const t0 = performance.now();
    while (frame < 64 && performance.now() - t0 < 5 && (!deadline || deadline.timeRemaining() > 1)) {
      writePlanetFrame(frame);
      frame++;
      planetReady = frame;
    }
    commit(planet);
    if (frame < 64) {
      const ric = window.requestIdleCallback;
      if (ric) ric((d) => step(d));
      else window.setTimeout(() => step(), 16);
    }
  };
  const ric = window.requestIdleCallback;
  if (ric) ric((d) => step(d));
  else window.setTimeout(() => step(), 16);
}

function layoutWindows() {
  const rows = !wide
    ? [[1, 1, 0, 0.02, 0.42], [0, 0, 1, 0.22, 0.12]]
    : low
      ? [[1, 1, 0, 0.5, 0.08], [2, 2, 0, 0.18, 0.84]]
      : [[0, 0, 1, 0.12, 0.5], [0, 0, 1, 0.36, 0.58], [1, 1, 0, 0.5, 0.08], [2, 2, 0, 0.18, 0.84]];
  for (let i = 0; i < 4; i++) {
    const w = wins[i];
    const row = rows[i];
    w.on = !!row;
    w.deadUntil = 0;
    w.variant = i % 3;
    w.phase = i * 0.8;
    w.bob = 3.1 + i * 0.45;
    w.glitchAt = t + 0.8 + i * 1.25;
    w.glitchUntil = 0;
    if (!row) continue;
    w.depth = row[0];
    w.size = row[1];
    w.far = row[2] === 1;
    w.yFrac = row[3];
    w.anchor = bufW * row[4];
  }
}

function schedule() {
  srand((0x57ec4a11 ^ Math.imul(loopId + 1, 0x9e3779b1)) >>> 0);
  const starGap = wide ? 4 : 6;
  const starJitter = wide ? 5 : 6;
  let at = 0.35 + rand() * 0.4;
  for (let i = 0; i < STAR_N; i++) {
    starAt[i] = at < LOOP ? at : 999;
    starFX[i] = rand();
    starFY[i] = rand();
    starAng[i] = rand();
    starLife[i] = rand();
    starComet[i] = i % 5 === 4 ? 1 : 0;
    at += starGap + rand() * starJitter;
  }
  const blastGap = low ? 12 : wide ? 6 : 9;
  const blastJitter = low ? 8 : wide ? 6 : 6;
  at = 1.1 + rand();
  for (let i = 0; i < BLAST_N; i++) {
    blastAt[i] = at < LOOP ? at : 999;
    blastFX[i] = rand();
    blastPick[i] = (rand() * 5) | 0;
    at += blastGap + rand() * blastJitter;
  }
  breakAt = t + 7 + rand() * 4;
  starI = 0;
  blastI = 0;
  while (starI < STAR_N && starAt[starI] <= t) starI++;
  while (blastI < BLAST_N && blastAt[blastI] <= t) blastI++;
}

function rebuild(w: number, h: number) {
  bufW = w;
  bufH = h;
  horizonY = Math.round(h * (portrait ? 0.86 : 0.8));
  groundH = Math.max(8, Math.round(h * (portrait ? 0.07 : 0.09)));
  cityY = h - groundH + 3;
  farH = Math.max(16, Math.round(h * 0.3));
  midH = Math.max(20, Math.round(h * 0.42));
  nebH = Math.max(12, Math.round(h * 0.55));
  sky = buildSky(w, h);
  flash = buildFlash(w);
  jobs = 0;
  if (nebKey !== `${nebH}`) jobs |= JOB_NEB;
  if (farKey !== `${farH}`) jobs |= JOB_FAR;
  if (midKey !== `${midH}`) jobs |= JOB_MID;
  if (gndKey !== `${groundH}`) jobs |= JOB_GND;
  nebX = 0;
  nebWork = null;
  if (!moon) moon = buildMoon();
  if (!cursorSpr[0]) buildCursor();
  if (nearSpr[0].length < 3) buildWindows();
  stars = attach(w, h);
  buildStars();
  for (let i = 0; i < 70; i++) {
    ringA[i] = (i / 70) * TAU + (hash(i + 4) % 100) / 700;
    ringKind[i] = i % 12 === 0 ? 2 : i % 3 === 0 ? 1 : 0;
    ringCol[i] = i % 5 === 0 ? 2 : i % 2 === 0 ? 0 : 1;
  }
  const r = portrait ? 26 : 20;
  if (r !== planetR || !planet) buildPlanet(r);
  view.canvas.width = w;
  view.canvas.height = h;
  view.imageSmoothingEnabled = false;
  dirty = true;
  rendered = false;
  layWide = wide;
  layLow = low;
  layoutWindows();
  schedule();
}

/** Build one slow cache per frame so startup never blocks for long. */
function runJob() {
  if (jobs & JOB_NEB) {
    if (!nebWork) {
      nebWork = attach(NEB_W, nebH);
      nebX = 0;
    }
    const next = nebX + 96;
    nebulaSlice(nebWork, nebX, next);
    nebX = next;
    if (nebX >= NEB_W) {
      commit(nebWork);
      nebula = nebWork;
      nebWork = null;
      nebKey = `${nebH}`;
      jobs &= ~JOB_NEB;
    }
  } else if (jobs & JOB_FAR) {
    const farG = canvas2d(FAR_W, farH);
    drawSkyline(farG, FAR_W, farH, true);
    shadeSkyline(farG, FAR_W, farH, true);
    farTile = farG.canvas;
    farKey = `${farH}`;
    jobs &= ~JOB_FAR;
  } else if (jobs & JOB_MID) {
    lightN = 0;
    spawnN = 0;
    antN = 0;
    const midG = canvas2d(MID_W, midH);
    drawSkyline(midG, MID_W, midH, false);
    shadeSkyline(midG, MID_W, midH, false);
    midTile = midG.canvas;
    midKey = `${midH}`;
    jobs &= ~JOB_MID;
  } else if (jobs & JOB_GND) {
    groundTile = buildGround(GND_W, groundH);
    gndKey = `${groundH}`;
    jobs &= ~JOB_GND;
  }
  dirty = true;
}

function syncLayout() {
  const r = portrait ? 26 : 20;
  if (r !== planetR) buildPlanet(r);
  if (wide !== layWide || low !== layLow) {
    layWide = wide;
    layLow = low;
    layoutWindows();
    schedule();
    dirty = true;
  }
}

function artShift(p: number): number {
  if (!playing) return 0;
  return (camX * p * camPix * 0.5 * bufW) / Math.max(1, destW);
}

function layerShift(menuSpeed: number, p: number, tile: number, drift: boolean): number {
  let x = 0;
  if (playing) x = artShift(p);
  else if (drift && !reduced) x = t * menuSpeed;
  return mod(x, tile);
}

function wrapSpan(): number {
  if (!wide) return bufW + 40;
  return Math.max(512, bufW + 24);
}

function winTravel(depth: number): number {
  if (playing) return artShift(LAYER_P[depth]);
  if (reduced) return 0;
  return t * SPEED[depth];
}

function winScreenX(w: Win): number {
  return mod(w.anchor - winTravel(w.depth), wrapSpan());
}

function ibob(period: number, phase: number): number {
  if (reduced) return 0;
  return Math.round(Math.sin((t / period + phase) * TAU));
}

function rect(x: number, y: number, w: number, h: number, fill: string) {
  view.fillStyle = fill;
  view.fillRect(x | 0, y | 0, w, h);
}

function tileAt(img: CanvasImageSource, tileW: number, y: number, shift: number) {
  let x0 = -shift % tileW;
  if (x0 > 0) x0 -= tileW;
  for (; x0 < bufW; x0 += tileW) view.drawImage(img, x0, y);
}

function starLevel(i: number): number {
  if (reduced) return starKind[i] === 2 ? 2 : 1;
  const period = starPeriod[i];
  const cycle = mod(t + starPhase[i] * period, period);
  const hold = 0.1;
  const up = (period - hold) * 0.48;
  const down = period - hold - up;
  let s: number;
  if (cycle < up) s = (cycle / up) * 2;
  else if (cycle < up + hold) s = 2;
  else s = 2 * (1 - (cycle - up - hold) / Math.max(0.001, down));
  return s >= 1.65 ? 2 : s >= 0.55 ? 1 : 0;
}

function starColor(i: number, level: number): number {
  const tint = starTint[i];
  if (level === 0) return STAR_DIM;
  if (level === 1) return tint === 1 ? WARM_MID : tint === 2 ? COOL_MID : STAR_MID;
  return tint === 1 ? GOLD : tint === 2 ? TEAL : PAPER;
}

function plot(data: Uint32Array, x: number, y: number, col: number) {
  if (x < 0 || y < 0 || x >= bufW || y >= bufH || !col) return;
  data[y * bufW + x] = col;
}

function paintStars() {
  if (!stars) return;
  stars.data.fill(0);
  const data = stars.data;
  const skip = low ? 1 : 0;
  for (let i = 0; i < starCount; i++) {
    if (skip && (i & 1)) continue;
    const level = starLevel(i);
    const col = starColor(i, level);
    const x = starX[i];
    const y = starY[i];
    plot(data, x, y, col);
    if (starKind[i] === 0 || level === 0) continue;
    plot(data, x - 1, y, col);
    plot(data, x + 1, y, col);
    plot(data, x, y - 1, col);
    plot(data, x, y + 1, col);
    if (starKind[i] === 2 && level === 2) {
      plot(data, x - 2, y, col);
      plot(data, x + 2, y, col);
      plot(data, x, y - 2, col);
      plot(data, x, y + 2, col);
    }
  }
  commit(stars);
}

function drawRing(cx: number, cy: number, back: boolean) {
  const rx = planetR * (34 / 20);
  const ry = planetR * (7 / 20);
  const tilt = (-12 * Math.PI) / 180;
  const ct = Math.cos(tilt);
  const st = Math.sin(tilt);
  const spin = reduced ? 0 : (t / 40) * TAU;
  for (let i = 0; i < 70; i++) {
    const a = ringA[i] + spin;
    const behind = Math.sin(a) > 0;
    if (behind !== back) continue;
    const ex = Math.cos(a) * rx;
    const ey = Math.sin(a) * ry;
    const x = Math.round(cx + ct * ex - st * ey);
    const y = Math.round(cy + st * ex + ct * ey);
    const dx = x - cx;
    const dy = y - cy;
    const dark = behind && dx > 0;
    const kind = ringKind[i];
    if (kind === 2) {
      rect(x, y, 3, 1, dark ? F.emberDeep : F.ember);
      rect(x, y + 1, 3, 1, dark ? F.mute : F.paper);
      continue;
    }
    const fill = dark ? F.mute : ringCol[i] === 2 ? F.ember : ringCol[i] === 0 ? F.paper : F.mute;
    if (kind === 1) rect(x, y, 2, 1, fill);
    else rect(x, y, 1, 1, fill);
  }
}

function placeMoon(cx: number, cy: number) {
  const a = reduced ? 0.4 : (t / 32) * TAU;
  const mx = Math.cos(a) * planetR * 1.85;
  const my = Math.sin(a) * planetR * 0.7;
  const tilt = 0.55;
  moonX = cx + Math.cos(tilt) * mx - Math.sin(tilt) * my;
  moonY = cy + Math.sin(tilt) * mx + Math.cos(tilt) * my;
  moonBehind = Math.sin(a) > 0;
}

function drawMoon(cx: number, cy: number, behindPass: boolean) {
  if (!moon || moonBehind !== behindPass) return;
  view.drawImage(moon, Math.round(moonX) - 5, Math.round(moonY) - 5);
  if (!moonBehind) {
    const dx = moonX - cx;
    const dy = moonY - cy;
    const len = Math.hypot(dx, dy) || 1;
    if (len < planetR + 6) {
      rect(cx + (dx / len) * (planetR - 2), cy + (dy / len) * (planetR - 2), 1, 2, F.core);
    }
  }
}

function buildCursor() {
  const keeps = [1, 0.75, 0.5, 0.25];
  const ox = [-1, 1, 0, 0];
  const oy = [0, 0, -1, 1];
  for (let k = 0; k < keeps.length; k++) {
    const g = canvas2d(7, 9);
    const keep = keeps[k];
    for (let pass = 0; pass < 2; pass++) {
      g.fillStyle = pass === 0 ? F.edge : F.paper;
      for (let row = 0; row < 7; row++) {
        const bits = ARROW[row];
        for (let col = 0; col < 5; col++) {
          if (((bits >> (4 - col)) & 1) === 0) continue;
          const n = pass === 0 ? 4 : 1;
          for (let i = 0; i < n; i++) {
            const px = 1 + col + (pass === 0 ? ox[i] : 0);
            const py = 1 + row + (pass === 0 ? oy[i] : 0);
            if (keep < 0.99 && bayer(px, py) > keep) continue;
            g.fillRect(px, py, 1, 1);
          }
        }
      }
    }
    cursorSpr[k] = g.canvas;
  }
}

function drawArrow(x: number, y: number, which: number) {
  const spr = cursorSpr[which];
  if (!spr) return;
  view.drawImage(spr, (x | 0) - 1, (y | 0) - 5);
}

function puff(x: number, y: number, fill: string, keep: number) {
  const x0 = x | 0;
  const y0 = y | 0;
  for (let i = 0; i < 9; i++) {
    const px = x0 + PUFF_X[i];
    const py = y0 + PUFF_Y[i];
    if (keep < 0.99 && bayer(px, py) > keep) continue;
    rect(px, py, 1, 1, fill);
  }
}

const SMOKE_DX = [-1, 1, -0.4, 0.6];
const SMOKE_DY = [-0.3, -0.5, 0.3, 0.1];

function drawBlast(b: Blast) {
  const age = t - b.t0;
  if (age < 0 || age > 1.25) {
    b.on = false;
    return;
  }
  const frame = (age * 12) | 0;
  const x = b.x;
  const y = b.y;
  const sc = b.scale;
  const small = sc < 1;
  if (frame >= 5) {
    const keep = frame < 9 ? 1 : 1 - (frame - 9) / 6;
    const rise = age * 9 * sc;
    const grow = Math.min(1, (frame - 4) / 3);
    const n = small ? 3 : 4;
    for (let i = 0; i < n; i++) {
      const px = x + SMOKE_DX[i] * (3 + grow * 3) * sc - 1;
      const py = y + SMOKE_DY[i] * 4 * sc - rise - 1;
      puff(px, py, F.smoke0, keep);
      puff(px + 1, py - 1, i & 1 ? F.smoke1 : F.smoke0, keep);
      if (frame < 9) puff(px + 1, py - 2, F.smoke2, keep * 0.6);
    }
  }
  if (frame === 0) {
    const r = Math.max(1, Math.round(1.2 * sc));
    rect(x - r, y - r, r * 2 + 1, r * 2 + 1, F.paper);
    rect(x - r - 2, y, 2, 1, F.gold);
    rect(x + r + 1, y, 2, 1, F.gold);
    rect(x, y - r - 2, 1, 2, F.gold);
    rect(x, y + r + 1, 1, 2, F.gold);
  } else if (frame === 1) {
    const len = Math.round(4 * sc);
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * TAU;
      const l = i & 1 ? len * 0.6 : len;
      for (let k = 1; k <= l; k++) rect(x + Math.cos(a) * k, y + Math.sin(a) * k, 1, 1, k > l - 1.5 ? F.ember : F.gold);
    }
    rect(x - 1, y - 1, 3, 3, F.paper);
  } else if (frame <= 4) {
    const rad = (3 + (frame - 2) * 2.6) * sc;
    const clumps = small ? 6 : 10;
    const ring = frame === 2 ? F.gold : frame === 3 ? F.ember : F.emberDeep;
    for (let i = 0; i < clumps; i++) {
      if (frame >= 3 && (i * 7 + frame) % 5 === 0) continue;
      const a = (i / clumps) * TAU + 0.35 + frame * 0.2;
      rect(x + Math.cos(a) * rad, y + Math.sin(a) * rad * 0.75, 2, 2, i % 3 === 0 ? F.gold : ring);
    }
    const core = Math.round((5 - frame) * 0.9 * sc);
    for (let dy = -core; dy <= core; dy++) {
      for (let dx = -core; dx <= core; dx++) {
        if (dx * dx + dy * dy > core * core) continue;
        const k = bayer(x + dx, y + dy);
        if (k > 0.55) continue;
        rect(x + dx, y + dy, 1, 1, k < 0.15 ? F.gold : k < 0.35 ? F.ember : F.emberDeep);
      }
    }
  }
  for (let i = 0; i < b.bits.length; i++) {
    const bit = b.bits[i];
    const by = bit.y0 + bit.vy * age * sc + 30 * age * age;
    const bx = bit.x0 + bit.vx * age * sc;
    if (bayer(bx, by) > 1 - age / 1.2) continue;
    rect(bx, by, 1, 1, i % 3 === 0 ? F.gold : i % 2 ? F.mute : F.ember);
  }
}

function drawShot(s: Shot) {
  const age = t - s.t0;
  if (age < 0 || age > s.max) {
    s.on = false;
    return;
  }
  const x = s.x0 + s.vx * age;
  const y = s.y0 + s.vy * age;
  if (s.comet) {
    drawArrow(x, y, 0);
    const sp = Math.hypot(s.vx, s.vy) || 1;
    const ux = s.vx / sp;
    const uy = s.vy / sp;
    for (let i = 0; i < 3; i++) {
      const dist = 6 + i * 4;
      drawArrow(x - ux * dist, y - uy * dist, i + 1);
    }
    return;
  }
  const sp = Math.hypot(s.vx, s.vy) || 1;
  const ux = s.vx / sp;
  const uy = s.vy / sp;
  const fade = age > s.max - 0.15 ? 0.5 : 1;
  for (let i = s.trail; i >= 1; i--) {
    const tx = x - ux * i;
    const ty = y - uy * i;
    const f = i / s.trail;
    if (f > 0.7 && bayer(tx, ty) > 0.5 * fade) continue;
    rect(tx, ty, 1, 1, f < 0.3 ? F.paper : f < 0.7 ? F.mid : F.dim);
    if (f < 0.3) rect(tx, ty - 1, 1, 1, F.dim);
  }
  rect(x - 1, y, 1, 1, F.mid);
  rect(x + 2, y, 1, 1, F.mid);
  rect(x, y - 1, 2, 1, F.mid);
  rect(x, y + 2, 2, 1, F.mid);
  rect(x, y, 2, 2, F.paper);
}

function drawWin(w: Win) {
  if (!w.on || t < w.deadUntil) return;
  const x = winScreenX(w) | 0;
  const y = (w.yFrac * bufH + ibob(w.bob, w.phase)) | 0;
  const spr = (w.far ? farSpr : nearSpr)[w.size][w.variant];
  if (!spr) return;
  const sw = SPR_W[w.size];
  const sh = SPR_H[w.size];
  view.drawImage(spr, x, y);
  if (!reduced && t < w.glitchUntil) {
    const row = Math.max(4, (sh / 2) | 0);
    view.drawImage(spr, 0, row, sw, 2, x + 2, y + row, sw, 2);
  }
  if (!reduced && t < w.flashUntil) rect(x + 1, y + 1, sw - 2, 3, F.ember);
}

function drawFrags() {
  for (let i = 0; i < frags.length; i++) {
    const f = frags[i];
    if (!f.on) continue;
    const age = t - f.t0;
    if (age < 0 || age > f.max) {
      f.on = false;
      continue;
    }
    const keep = 1 - age / f.max;
    const x = f.x0 + f.vx * age;
    const y = f.y0 + f.vy * age + 20 * age * age;
    if (bayer(x, y) > keep) continue;
    const swap = ((age * 8) | 0) & 1;
    const w = swap ? f.h : f.w;
    const h = swap ? f.w : f.h;
    rect(x, y, w, h, f.fill);
    rect(x, y, w, 1, F.edge);
  }
}

function gutterX(side: number, frac: number): number {
  if (!wide) return frac * bufW;
  return side ? bufW * (0.78 + frac * 0.18) : bufW * (0.04 + frac * 0.16);
}

function inGutter(x: number): boolean {
  if (!wide) return true;
  const f = x / Math.max(1, bufW);
  return f < 0.24 || f > 0.76;
}

function spawnShot(comet: boolean, x: number, y: number, ang: number, life: number) {
  const s = !shots[0].on ? shots[0] : !shots[1].on ? shots[1] : null;
  if (!s) return;
  s.on = true;
  s.comet = comet;
  s.x0 = x;
  s.y0 = y;
  s.vx = -Math.cos(ang) * 140;
  s.vy = Math.sin(ang) * 140;
  s.t0 = t;
  s.max = life;
  s.trail = 18 + (hash((x * 3) | 0) % 9);
}

function spawnBlast(x: number, y: number, scale: number) {
  const b = !blasts[0].on ? blasts[0] : !blasts[1].on ? blasts[1] : null;
  if (!b) return;
  b.on = true;
  b.x = x | 0;
  b.y = y | 0;
  b.t0 = t;
  b.scale = scale;
  for (let i = 0; i < b.bits.length; i++) {
    const bit = b.bits[i];
    bit.x0 = b.x;
    bit.y0 = b.y;
    bit.vx = -28 + i * 8;
    bit.vy = -46 - (i % 3) * 8;
  }
  flashUntil = t + 0.1;
  lastBlast = t;
}

function shatter(w: Win) {
  const x = winScreenX(w);
  const y = w.yFrac * bufH;
  spawnBlast(x + SPR_W[w.size] * 0.5, y + 4, playing ? 0.65 : 1.4);
  const cuts = [
    [1, 1, 8, 5],
    [10, 2, 8, 4],
    [3, 7, 7, 4],
    [12, 8, 6, 4],
    [2, 12, 6, 3],
    [14, 12, 7, 3],
    [6, 4, 5, 3],
    [16, 5, 5, 3],
  ];
  let n = 0;
  for (let i = 0; i < frags.length && n < 8; i++) {
    const f = frags[i];
    if (f.on) continue;
    const c = cuts[n];
    f.on = true;
    f.x0 = x + c[0] * (SPR_W[w.size] / 38);
    f.y0 = y + c[1] * (SPR_H[w.size] / 24);
    f.w = Math.max(2, (c[2] * SPR_W[w.size]) / 38);
    f.h = Math.max(2, (c[3] * SPR_H[w.size]) / 24);
    f.vx = -36 + n * 12;
    f.vy = -28 - (n % 3) * 6;
    f.t0 = t;
    f.max = 1.2;
    f.fill = n % 3 === 0 ? F.ember : n % 3 === 1 ? F.mute : F.paper;
    n++;
  }
  const wait = 4 + (hash(breakN * 13 + 1) % 4000) / 1000;
  const when = t + wait;
  w.deadUntil = when;
  w.anchor = bufW + 12 + SPEED[w.depth] * when;
  w.glitchUntil = 0;
}

function pickBlastXY(i: number) {
  const side = i & 1;
  const frac = blastFX[i] || 0.2;
  pickX = gutterX(side, frac);
  pickY = horizonY - 4;
  if (spawnN > 0 && blastPick[i] !== 4) {
    const s = i % spawnN;
    const shift = layerShift(8, 0.12, MID_W, true);
    pickX = mod(spawnX[s] - shift, MID_W);
    pickY = cityY - midH + altDrop * 0.65 + spawnY[s];
    if (wide && !inGutter(pickX)) pickX = gutterX(side, frac);
  }
}

function handlePoke() {
  if (!poke) return;
  const kind = poke;
  poke = "";
  if (kind === "star" || kind === "comet") {
    const ang = ((kind === "comet" ? 24 : 30) * Math.PI) / 180;
    spawnShot(kind === "comet", bufW * 0.9, bufH * 0.12, ang, 0.85);
    return;
  }
  if (kind === "blast") {
    spawnBlast(wide ? bufW * 0.1 : bufW * 0.5, horizonY - 8, wide ? 1.5 : 1.2);
    return;
  }
  if (kind === "glitch") {
    for (let i = 0; i < wins.length; i++) {
      if (!wins[i].on) continue;
      wins[i].glitchUntil = t + 0.08;
      wins[i].flashUntil = t + 0.034;
      break;
    }
    return;
  }
  for (let i = 0; i < wins.length; i++) {
    const w = wins[i];
    if (!w.on || w.depth === 0) continue;
    w.deadUntil = 0;
    w.anchor = winTravel(w.depth) + bufW * 0.1;
    shatter(w);
    break;
  }
}

function simulate() {
  stepDt = 0;
  accum = 0;
  handlePoke();
  if (reduced) return;
  while (starI < STAR_N && starAt[starI] <= t) {
    const i = starI++;
    if (playing && (i & 1)) continue;
    const ang = ((20 + starAng[i] * 15) * Math.PI) / 180;
    const x = wide ? bufW * (0.76 + starFX[i] * 0.22) : starFX[i] * bufW;
    const y = starFY[i] * bufH * 0.32;
    spawnShot(starComet[i] === 1, x, y, ang, 0.6 + starLife[i] * 0.3);
  }
  while (blastI < BLAST_N && blastAt[blastI] <= t) {
    const i = blastI++;
    if (playing && t - lastBlast < 15) continue;
    pickBlastXY(i);
    spawnBlast(pickX, pickY, playing ? 0.6 : wide ? 1.5 : 1.2);
  }
  if (!playing && t >= breakAt) {
    breakN++;
    breakAt = t + 18 + (hash(breakN * 17 + loopId) % 8000) / 1000;
    for (let i = 0; i < wins.length; i++) {
      const w = wins[i];
      if (!w.on || w.depth === 0 || t < w.deadUntil) continue;
      const x = winScreenX(w);
      if (!inGutter(x + SPR_W[w.size] * 0.5)) continue;
      shatter(w);
      break;
    }
  }
  for (let i = 0; i < wins.length; i++) {
    const w = wins[i];
    if (!w.on || t < w.deadUntil) continue;
    if (t >= w.glitchAt) {
      w.glitchUntil = t + 0.08;
      w.flashUntil = t + 0.034;
      w.glitchN++;
      w.glitchAt = t + 3 + (hash(w.glitchN * 29 + i * 7 + loopId) % 4000) / 1000;
    }
  }
  const sec = t | 0;
  if (sec !== lightSec) {
    lightSec = sec;
    for (let i = 0; i < lightN; i++) {
      if (hash(i * 131 + sec) % 50 === 0) lightOn[i] ^= 1;
    }
  }
}

function drawEmbers() {
  if (reduced) return;
  const n = playing || !wide || low ? 4 : 8;
  const fire = bufH - groundH - 1 + altDrop;
  for (let i = 0; i < n; i++) {
    const span = 3 + (hash(i * 5 + 2) % 200) / 100;
    const speed = 4 + (hash(i * 9 + 1) % 5);
    const u = mod(t * 0.5 + (hash(i * 3) % 1000) / 90, span);
    let y0 = fire;
    let x = hash(i * 13 + 4) % bufW;
    if ((i & 1) === 0 && spawnN > 0) {
      const s = i % spawnN;
      const shift = layerShift(8, 0.12, MID_W, true);
      x = mod(spawnX[s] - shift, MID_W);
      y0 = cityY - midH + altDrop * 0.65 + spawnY[s];
    }
    const y = Math.round(y0 - u * speed);
    const sx = (x + Math.round(Math.sin(t * 1.6 + i))) | 0;
    if (y < 2 || y >= bufH) continue;
    const keep = 1 - u / span;
    if (bayer(sx, y) > 0.12 + keep * 0.88) continue;
    rect(sx, y, 1, 1, hash(i) % 4 === 0 ? F.gold : F.ember);
  }
}

function render() {
  if (!sky || !stars || !planet) return;
  const m0 = performance.now();
  const farDrop = altDrop * 0.35;
  const midDrop = altDrop * 0.65;
  view.drawImage(sky.c, 0, 0);
  paintStars();
  view.drawImage(stars.c, 0, 0);
  const nebShift = layerShift(2.5, 0.02, NEB_W, !low);
  if (nebula && !(jobs & JOB_NEB)) tileAt(nebula.c, NEB_W, 0, nebShift);
  if (t < flashUntil && flash) view.drawImage(flash.c, 0, Math.round(bufH * 0.83 + farDrop));
  const farShift = layerShift(3, 0.05, FAR_W, true);
  if (farTile && !(jobs & JOB_FAR)) tileAt(farTile, FAR_W, cityY - 2 - farH + farDrop, farShift);
  for (let i = 0; i < wins.length; i++) if (wins[i].on && wins[i].depth === 0) drawWin(wins[i]);

  const pcx = Math.round((wide ? bufW * 0.12 : bufW * 0.82) + artShift(0.01));
  const pcy = Math.round((wide ? bufH * 0.25 : bufH * 0.06) + ibob(8, 0) + altDrop * 0.15);
  placeMoon(pcx, pcy);
  drawRing(pcx, pcy, true);
  drawMoon(pcx, pcy, true);
  const frame = reduced ? 0 : (Math.floor(reduced ? 0 : t) % Math.max(1, planetReady));
  const side = planetSide;
  view.drawImage(planet.c, 0, frame * side, side, side, pcx - planetR - 1, pcy - planetR - 1, side, side);
  drawRing(pcx, pcy, false);
  drawMoon(pcx, pcy, false);

  const midShift = layerShift(8, 0.12, MID_W, true);
  const midY = cityY - midH + midDrop;
  const midOk = !!midTile && !(jobs & JOB_MID);
  if (midTile && midOk) tileAt(midTile, MID_W, midY, midShift);
  const lit = [F.ember, F.paper, F.gold, F.teal];
  for (let i = 0; i < (midOk ? lightN : 0); i++) {
    if (!lightOn[i]) continue;
    const x = mod(lightX[i] - midShift, MID_W);
    const y = midY + lightY[i];
    if (x >= bufW || y < 0 || y >= bufH) continue;
    rect(x, y, 1, 1, lit[lightTint[i]]);
  }
  if (!reduced) {
    const blink = (t % 1) < 0.6;
    if (blink) {
      for (let i = 0; i < (midOk ? antN : 0); i++) {
        const x = mod(antX[i] - midShift, MID_W);
        rect(x, midY + antY[i], 1, 1, F.red);
      }
    }
  }
  for (let i = 0; i < wins.length; i++) if (wins[i].on && wins[i].depth !== 0) drawWin(wins[i]);
  drawEmbers();
  for (let i = 0; i < shots.length; i++) if (shots[i].on) drawShot(shots[i]);
  for (let i = 0; i < blasts.length; i++) if (blasts[i].on) drawBlast(blasts[i]);
  drawFrags();

  const gShift = layerShift(15, 0.25, GND_W, true);
  const gy = bufH - groundH + altDrop;
  const gOk = !!groundTile && !(jobs & JOB_GND);
  if (groundTile && gOk) tileAt(groundTile, GND_W, gy, gShift);
  for (let i = 0; i < (gOk ? gemN : 0); i++) {
    const flicker = reduced || hash((((t * 7) | 0) + i * 13)) % 3 !== 0;
    if (!flicker) continue;
    const x = mod(gemX[i] - gShift, GND_W);
    rect(x, gy + gemY[i], 1, 1, i % 2 ? F.gold : F.ember);
  }
  lastMs = performance.now() - m0;
  dirty = false;
  rendered = true;
}

function advance(now: number) {
  if (!stamped) {
    stamped = true;
    stamp = now;
    return;
  }
  if (hidden || reduced) {
    stamp = now;
    return;
  }
  let dt = (now - stamp) / 1000;
  stamp = now;
  if (!(dt > 0)) return;
  if (dt > 0.05) dt = 0.05;
  stepDt += dt;
  accum += dt;
  t += dt;
  if (t >= LOOP) {
    t -= LOOP;
    loopId++;
    schedule();
    for (let i = 0; i < wins.length; i++) wins[i].glitchAt = t + 0.6 + i;
  }
}

function shouldDraw(): boolean {
  if (!rendered || dirty || poke) return true;
  if (reduced) return playing && (camX !== lastCam || altDrop !== lastAlt);
  return accum >= 1 / 30;
}

function readLow(now: number) {
  if (now - lowCheck < 400) return;
  lowCheck = now;
  const next = localStorage.getItem("daw:quality") === "low";
  if (next !== low) {
    low = next;
    dirty = true;
  }
}

let lowCheck = 0;

export function skySetHidden(value: boolean) {
  if (hidden === value) return;
  hidden = value;
  stamped = false;
}

export function skySetReduced(value: boolean) {
  if (reduced === value) return;
  reduced = value;
  dirty = true;
  stamped = false;
}

export function skyTime(): number {
  return t;
}

export function skyCost(): number {
  return lastMs;
}

export function skyStar() {
  poke = "star";
  dirty = true;
}
export function skyComet() {
  poke = "comet";
  dirty = true;
}
export function skyBlast() {
  poke = "blast";
  dirty = true;
}
export function skyBreak() {
  poke = "break";
  dirty = true;
}
export function skyGlitch() {
  poke = "glitch";
  dirty = true;
}

/** Draw the procedural night into the destination canvas. `live` follows the gameplay camera. */
export function paintWreck(
  ctx: CanvasRenderingContext2D,
  destWidth: number,
  destHeight: number,
  cameraX: number,
  pix: number,
  altitude: number,
  now: number,
  live: boolean,
) {
  if (destWidth < 2 || destHeight < 2) return;
  const canvas = ctx.canvas;
  const cssW = canvas.clientWidth || Math.max(1, Math.round(destWidth / (window.devicePixelRatio || 1)));
  const cssH = canvas.clientHeight || Math.max(1, Math.round(destHeight / (window.devicePixelRatio || 1)));
  portrait = cssH > cssW;
  wide = isWide(cssW, cssH);
  playing = live;
  camX = cameraX;
  camPix = pix;
  destW = destWidth;
  const bw = Math.max(8, Math.ceil(cssW / artStep(cssW, cssH)));
  const bh = Math.max(8, Math.ceil(cssH / artStep(cssW, cssH)));
  const nowMs = now || performance.now();
  readLow(nowMs);
  if (bw !== bufW || bh !== bufH) rebuild(bw, bh);
  wide = isWide(cssW, cssH);
  altDrop = clamp(altitude * 14, 0, bufH * 0.18);
  syncLayout();
  if (hidden) {
    if (rendered) {
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(view.canvas, 0, 0, destWidth, destHeight);
    }
    return;
  }
  advance(nowMs);
  if (jobs) runJob();
  if (shouldDraw()) {
    if (!reduced || poke) simulate();
    lastCam = camX;
    lastAlt = altDrop;
    render();
  }
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(view.canvas, 0, 0, destWidth, destHeight);
}
